import React, { useState } from 'react';
import { ChevronDown, ChevronUp, PauseCircle, PlayCircle, Trash2 } from 'lucide-react';
import Input from '../../../components/ui/Input';
import Textarea from '../../../components/ui/Textarea';
import ConfirmModal from '../../../components/ui/ConfirmModal';
import TextFieldActions from '../../../components/textVersions/TextFieldActions';
import IconActionButton from '../../../components/ui/IconActionButton';
import { buildTextFieldStackKey } from '../../../utils/resourceIds';
import FormOptionCard from '../../../components/form/FormOptionCard';
import FormSegmentedControl from '../../../components/form/FormSegmentedControl';
import { isPersistedSubAgent } from '../../Agents/formUtils';
import {
  buildSubAgentTypeChangeMessage,
  EXECUTION_PHASE_LABELS,
  FIXED_EXECUTION_PHASE_BY_TYPE,
  getExecutionPhase,
  getSubAgentDisplayLabel,
  getSubAgentTypeLabel,
  isSubAgentIncomplete,
  SKILLS_EXECUTION_PHASE_OPTIONS,
  SUB_AGENT_TYPE_CARD_OPTIONS,
} from '../constants';
import { SUB_AGENT_TYPE_ICONS } from '../../../support/iconMappings';
import KnowledgeBaseMultiSelect from './KnowledgeBaseMultiSelect';
import SubAgentAdvancedOptions from './SubAgentAdvancedOptions';

function instructionSummary(instructions) {
  const trimmed = instructions?.trim();
  if (!trimmed) {
    return 'Sem instruções';
  }
  return trimmed.length > 80 ? `${trimmed.slice(0, 80)}…` : trimmed;
}

function SubAgentCard({
  subAgent,
  index,
  isActive,
  expanded,
  onToggleExpand,
  onRemove,
  onChange,
  onRequestImproveInstructions,
  onRequestImproveMcpInstructions,
  isSubmitting,
  isImproveModalSubmitting,
  isApplyingImprovePreview = false,
  improvingSubAgentId,
  improvingMcpBindingId = null,
  textVersionStack,
  improveError,
  onClearImproveError,
  getMcpImproveError,
  knowledgeBases,
  defaultKnowledgeBaseId,
  llmModelLines,
  llmEfforts,
  mcpServers = [],
  subAgents = [],
}) {
  const [pendingType, setPendingType] = useState(null);
  const fixedPhase = FIXED_EXECUTION_PHASE_BY_TYPE[subAgent.type];
  const typeLabel = getSubAgentTypeLabel(subAgent.type);
  const displayLabel = getSubAgentDisplayLabel(subAgent, index);
  const incomplete = isSubAgentIncomplete(subAgent);
  const enabled = subAgent.enabled !== false;
  const TypeIcon = SUB_AGENT_TYPE_ICONS[subAgent.type];
  const instructionsStackKey = buildTextFieldStackKey(
    'sub_agent',
    subAgent.id,
    'instructions'
  );

  const handleUndoInstructions = () => {
    if (!textVersionStack) {
      return;
    }
    const previous = textVersionStack.undo(instructionsStackKey);
    if (previous !== null) {
      onChange(subAgent.id, { instructions: previous });
    }
  };

  const applyTypeChange = (newType) => {
    const patch = { type: newType };

    if (newType === 'skills') {
      patch.executionPhase =
        subAgent.type === 'skills' && subAgent.executionPhase === 'post_response'
          ? 'post_response'
          : 'pre_response';
    } else {
      patch.executionPhase = '';
    }

    if (!subAgent.knowledgeBaseIds?.length && defaultKnowledgeBaseId) {
      patch.knowledgeBaseIds = [defaultKnowledgeBaseId];
    }

    onChange(subAgent.id, patch);
  };

  // Em sub-agentes já salvos a troca de tipo muda a topologia do agente,
  // então pede confirmação antes de aplicar.
  const handleTypeChange = (newType) => {
    if (newType === subAgent.type) {
      return;
    }
    if (isPersistedSubAgent(subAgent.id)) {
      setPendingType(newType);
      return;
    }
    applyTypeChange(newType);
  };

  const handleConfirmTypeChange = () => {
    applyTypeChange(pendingType);
    setPendingType(null);
  };

  const handleImproveClick = () => {
    const draft = subAgent.instructions.trim();
    if (!draft) {
      onClearImproveError(subAgent.id, 'Escreva um rascunho antes de melhorar o texto.');
      return;
    }
    onClearImproveError(subAgent.id, '');
    onRequestImproveInstructions(subAgent);
  };

  return (
    <div
      className={`relative overflow-hidden rounded-xl border transition-all duration-200 ${
        isActive
          ? 'border-forge-300/80 shadow-[0_4px_14px_-6px_rgba(14,165,233,0.16)]'
          : 'border-surface-border hover:border-slate-300'
      } ${enabled ? 'bg-white' : 'bg-slate-50/80'}`}
    >
      {isActive && (
        <div
          className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-forge-400 to-forge-600"
          aria-hidden
        />
      )}
      <div className={`flex items-start justify-between gap-3 py-3 pr-4 ${isActive ? 'pl-5' : 'pl-4'}`}>
        <button
          type="button"
          onClick={onToggleExpand}
          className="min-w-0 flex-1 text-left"
          aria-expanded={expanded}
        >
          <div className="flex items-start gap-2.5">
            {TypeIcon && (
              <span
                className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border ${
                  isActive
                    ? 'border-forge-200 bg-forge-100 text-forge-700'
                    : 'border-surface-border bg-slate-50 text-content-muted'
                }`}
              >
                <TypeIcon className="h-4 w-4" aria-hidden />
              </span>
            )}
            <span className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-medium text-content-primary">{displayLabel}</p>
                {isActive && (
                  <span className="inline-flex items-center rounded-full border border-forge-200 bg-forge-50 px-2 py-0.5 text-[10px] font-medium text-forge-700">
                    Em edição
                  </span>
                )}
                {!enabled && (
                  <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">
                    Pausado
                  </span>
                )}
                {incomplete && (
                  <span
                    className="h-2 w-2 shrink-0 rounded-full bg-amber-400"
                    title="Instruções pendentes"
                    aria-label="Instruções pendentes"
                  />
                )}
              </div>
              <p className="text-xs text-content-muted mt-0.5">{typeLabel}</p>
              <p className="text-xs text-content-muted mt-0.5 truncate">
                {instructionSummary(subAgent.instructions)}
              </p>
            </span>
          </div>
        </button>

        <div className="flex shrink-0 items-center gap-1.5">
          <IconActionButton
            icon={enabled ? PauseCircle : PlayCircle}
            label={enabled ? 'Pausar' : 'Reativar'}
            onClick={() => onChange(subAgent.id, { enabled: !enabled })}
            className={
              enabled
                ? 'hover:border-amber-200 hover:bg-amber-50 hover:text-amber-700'
                : 'border-emerald-200 text-emerald-700 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-800'
            }
          />
          <IconActionButton
            icon={expanded ? ChevronUp : ChevronDown}
            label={expanded ? 'Recolher' : 'Expandir'}
            onClick={onToggleExpand}
          />
          <IconActionButton
            icon={Trash2}
            label="Remover"
            onClick={onRemove}
            variant="danger"
          />
        </div>
      </div>

      {expanded && (
        <div className={`space-y-4 border-t border-surface-border py-4 pr-4 ${isActive ? 'pl-5' : 'pl-4'}`}>
          <div className="rounded-lg border border-forge-100 bg-forge-50/60 px-3 py-2">
            <p className="text-xs text-forge-800">
              <span className="font-semibold">Editando:</span> {displayLabel} · {typeLabel}
            </p>
            {!enabled && (
              <p className="mt-1 text-xs text-amber-700">
                Este comportamento está pausado e não será executado. As configurações ficam
                preservadas.
              </p>
            )}
          </div>

          <Input
            id={`sub_agent_name_${subAgent.id}`}
            label="Nome do comportamento (opcional)"
            value={subAgent.name || ''}
            onChange={(e) => onChange(subAgent.id, { name: e.target.value })}
            placeholder="Ex.: FAQ do produto"
            hint="Ajuda a identificar este comportamento quando houver vários do mesmo tipo."
          />

          <div>
            <p className="text-sm font-medium text-slate-700 mb-2">Tipo de comportamento</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {SUB_AGENT_TYPE_CARD_OPTIONS.map((option) => (
                <FormOptionCard
                  key={option.value}
                  selected={subAgent.type === option.value}
                  title={option.label}
                  description={option.description}
                  icon={SUB_AGENT_TYPE_ICONS[option.value]}
                  onClick={() => handleTypeChange(option.value)}
                />
              ))}
            </div>
          </div>

          <KnowledgeBaseMultiSelect
            id={`sub_agent_kbs_${subAgent.id}`}
            label="Bases de conhecimento (opcional)"
            knowledgeBases={knowledgeBases}
            selectedIds={subAgent.knowledgeBaseIds || []}
            onChange={(knowledgeBaseIds) => onChange(subAgent.id, { knowledgeBaseIds })}
            hint="Opcional. Quando selecionadas, o comportamento consulta essas bases via RAG."
          />

          {subAgent.type === 'skills' ? (
            <FormSegmentedControl
              id={`sub_agent_phase_${subAgent.id}`}
              label="Quando executar"
              value={getExecutionPhase(subAgent)}
              options={SKILLS_EXECUTION_PHASE_OPTIONS}
              onChange={(executionPhase) => onChange(subAgent.id, { executionPhase })}
              hint="Define se o comportamento roda antes ou depois da resposta principal."
            />
          ) : (
            <div className="rounded-xl border border-surface-border bg-slate-50 px-4 py-3">
              <p className="text-xs font-medium text-content-muted">Quando executar</p>
              <p className="text-sm text-content-primary mt-1">
                {EXECUTION_PHASE_LABELS[fixedPhase]}
              </p>
              <p className="text-xs text-content-muted mt-1">
                Definida automaticamente para este tipo de comportamento.
              </p>
            </div>
          )}

          <Textarea
            id={`sub_agent_instructions_${subAgent.id}`}
            label="Instruções"
            value={subAgent.instructions}
            onChange={(e) => {
              onChange(subAgent.id, { instructions: e.target.value });
              if (improveError) {
                onClearImproveError(subAgent.id, '');
              }
            }}
            placeholder="Descreva o que este comportamento deve fazer..."
            hint="Use o ícone de IA no canto do campo para refinar as instruções."
            required
            rows={4}
            action={{
              iconOnly: true,
              ariaLabel: 'Melhorar instruções com IA',
              loading: improvingSubAgentId === subAgent.id,
              disabled:
                isSubmitting ||
                isImproveModalSubmitting ||
                isApplyingImprovePreview ||
                improvingMcpBindingId !== null ||
                (improvingSubAgentId !== null && improvingSubAgentId !== subAgent.id),
              onClick: handleImproveClick,
              error: improveError,
            }}
          />
          <TextFieldActions
            stackKey={instructionsStackKey}
            canUndo={textVersionStack?.canUndo(instructionsStackKey)}
            onUndo={handleUndoInstructions}
            resourceType="sub_agent"
            resourceId={subAgent.id}
            fieldName="instructions"
            onRestoreFromHistory={(text) => onChange(subAgent.id, { instructions: text })}
            disabled={isSubmitting || isImproveModalSubmitting || isApplyingImprovePreview}
          />

          <SubAgentAdvancedOptions
            subAgent={subAgent}
            onChange={onChange}
            llmModelLines={llmModelLines}
            llmEfforts={llmEfforts}
            mcpServers={mcpServers}
            onRequestImproveMcpInstructions={onRequestImproveMcpInstructions}
            improvingMcpBindingId={improvingMcpBindingId}
            isMcpImproveDisabled={
              isSubmitting ||
              isImproveModalSubmitting ||
              isApplyingImprovePreview ||
              improvingSubAgentId !== null
            }
            getMcpImproveError={getMcpImproveError}
            subAgents={subAgents}
          />
        </div>
      )}

      <ConfirmModal
        open={Boolean(pendingType)}
        title="Trocar o tipo do comportamento"
        message={
          pendingType ? buildSubAgentTypeChangeMessage(subAgent, pendingType) : ''
        }
        confirmLabel="Trocar tipo"
        cancelLabel="Cancelar"
        onConfirm={handleConfirmTypeChange}
        onCancel={() => setPendingType(null)}
      />
    </div>
  );
}

export default SubAgentCard;
